/**
 * Request-type field registry
 *
 * Maps a Task `requestType` to the extra fields it captures. Each definition is
 * EITHER `component` (a custom React group, e.g. the IO Cascader) OR `fields` (a
 * declarative list rendered by DynamicRequestFields). `serialize` / `populate`
 * convert to/from the `Task.requestTypeDetails` Json; declarative types get sane
 * defaults so adding a simple new type is just an entry here — no new file and no
 * changes to TaskFormContainer.
 *
 * Definition shape:
 *   {
 *     component?: ReactComponent,        // custom create/edit-FORM UI (gets { form, userOrgId })
 *     fields?: FieldDescriptor[],        // declarative create/edit fields (see DynamicRequestFields)
 *     DetailsView?: ReactComponent,      // custom READ view (gets { details, task, hideNote });
 *                                        // omit for `fields` types — DynamicRequestDetails renders them
 *     EditFields?: ReactComponent,       // custom inline-EDIT fields (gets { form }); omit for `fields`
 *                                        // types — DynamicRequestEditFields renders them
 *     detailsTitle?: string,             // heading for the generic read/edit card (declarative types)
 *     hideOrgFields?: boolean,           // hide the generic Channel Partner/Advertiser row
 *     hideDescription?: boolean,         // drop the Task Description section; the type's own
 *                                        // field (e.g. the IO Note) replaces it, and the form
 *                                        // passes the attachment UI in as `attachmentsSlot`
 *     mentionField?: string,             // name of a free-text field supporting @mentions; on
 *                                        // inline edit only mentions ADDED there ping (avoids re-ping)
 *     serialize?: (values) => object,    // form values  -> requestTypeDetails Json
 *     populate?: (details, task) => obj, // requestTypeDetails -> form values (edit)
 *     applyToTask?: (values, data) => void, // side effects on the Task mutation data
 *   }
 *
 * Read/inline-edit surfaces (drawer, details page, mobile sheet) dispatch on this def:
 * a `DetailsView`/`EditFields` wins; otherwise a `fields` type renders generically. So a
 * simple new type is genuinely just a `fields` entry — it shows up on the form AND the
 * read/inline-edit surfaces with no new component.
 */

import moment from 'moment';
import TaskIORequestFields from './TaskIORequestFields';
import TaskIODetailsView from './TaskIODetailsView';
import TaskIOEditFields from './TaskIOEditFields';
import DynamicRequestFields from './DynamicRequestFields';
import { hasValue } from './fieldHelpers';
import { IO_REQUEST_TYPE } from '../../../configs/task.config';

const toISO = (m) => (m && m.toISOString ? m.toISOString() : m || null);
const toMoment = (v) => (v ? moment(v) : null);
// First value that was actually entered, else null. Used to read a money field from the deal
// type's own name OR its counterpart in the other layout.
const firstEntered = (...values) => {
  const entered = values.find(hasValue);
  return entered === undefined ? null : entered;
};

// Default serialize/populate for declarative (`fields`) types, using each
// descriptor's `type` to convert dates to/from ISO strings.
const defaultSerialize = (fields = []) => (values = {}) =>
  fields.reduce((acc, f) => {
    const val = values[f.name];
    if (f.type === 'date') acc[f.name] = toISO(val);
    else if (f.type === 'daterange') acc[f.name] = Array.isArray(val) ? val.map(toISO) : null;
    else acc[f.name] = val ?? null;
    return acc;
  }, {});

const defaultPopulate = (fields = []) => (details = {}) =>
  fields.reduce((acc, f) => {
    const val = details ? details[f.name] : undefined;
    if (f.type === 'date') acc[f.name] = val ? toMoment(val) : undefined;
    else if (f.type === 'daterange')
      acc[f.name] = Array.isArray(val) ? val.map(toMoment) : undefined;
    else acc[f.name] = val;
    return acc;
  }, {});

const registry = {
  [IO_REQUEST_TYPE]: {
    component: TaskIORequestFields, // custom create/edit form fields
    DetailsView: TaskIODetailsView, // custom read view (dealType branch, flight range, note)
    EditFields: TaskIOEditFields, // custom inline-edit fields (auto months, currency, dealType)
    detailsTitle: 'CAMPAIGN SETUP DETAILS', // heading for the inline-edit card
    hideDescription: true, // the Note (+ inline attachments) replaces the description
    mentionField: 'note', // free-text field that supports @mentions (drives edit-notify diffing)

    serialize: (v = {}) => {
      // Deal type is derived in Task Details (direct-level partner or self-named advertiser =>
      // DIRECT, otherwise CHANNEL_PARTNER) and carried on the form as `dealType`. Every IO field
      // is optional — missing values serialize to null.
      const dealType = v.dealType === 'CHANNEL_PARTNER' ? 'CHANNEL_PARTNER' : 'DIRECT';
      const [start, end] = v.flightDates || [];
      return {
        dealType,
        flightStartDate: toISO(start),
        flightEndDate: toISO(end),
        numberOfMonths: v.numberOfMonths ?? null,
        website: v.website || null,
        clickthroughUrl: v.clickthroughUrl || null,
        note: v.note || null,
        // Each deal type keeps its money in its own fields, so fall back to the counterpart in
        // the other layout: the deal type can be derived (or corrected) AFTER the user has
        // typed, and what they entered must not be dropped just because it landed on the
        // fields the other layout shows (issue #11760).
        ...(dealType === 'CHANNEL_PARTNER'
          ? {
              // CHANNEL PARTNER DETAILS = CPM + Total Budget; ADVERTISER DETAILS = CPM only.
              partnerCpm: firstEntered(v.partnerCpm, v.cpm),
              partnerTotalBudget: firstEntered(v.partnerTotalBudget, v.totalBudget),
              advertiserCpm: v.advertiserCpm ?? null,
            }
          : {
              cpm: firstEntered(v.cpm, v.partnerCpm),
              totalBudget: firstEntered(v.totalBudget, v.partnerTotalBudget),
            }),
      };
    },
    // clientPath / channelPartnerId / advertiserId are populated by the container's standard
    // edit flow (the cascader lives in Task Details now); here we only restore the IO fields.
    populate: (d = {}) => ({
      // Restore the SAVED dealType so the correct money fields show and re-serialize. Without
      // this it was re-derived from the (incompletely loaded) org list on edit and could flip
      // to DIRECT, nulling out partnerCpm/partnerTotalBudget/advertiserCpm on save.
      dealType: d && d.dealType,
      flightDates:
        d && d.flightStartDate && d.flightEndDate
          ? [toMoment(d.flightStartDate), toMoment(d.flightEndDate)]
          : undefined,
      numberOfMonths: d && d.numberOfMonths,
      website: d && d.website,
      clickthroughUrl: d && d.clickthroughUrl,
      note: d && d.note,
      partnerCpm: d && d.partnerCpm,
      partnerTotalBudget: d && d.partnerTotalBudget,
      advertiserCpm: d && d.advertiserCpm,
      cpm: d && d.cpm,
      totalBudget: d && d.totalBudget,
    }),
    applyToTask: (v = {}, data = {}) => {
      const start = v.flightDates && v.flightDates[0];
      if (start) data.startDate = start; // mirror flight-start to task.startDate
    },
  },

  // ---------------------------------------------------------------------------
  // To add a SIMPLE new request type, drop an entry like this (no new file). It is
  // then rendered on the create/edit FORM, the read surfaces, AND inline edit:
  //
  // 'SEO Audit': {
  //   detailsTitle: 'SEO AUDIT DETAILS',
  //   fields: [
  //     { name: 'targetUrl',  label: 'Target URL',  type: 'url',       span: 12, required: true },
  //     { name: 'keywords',   label: 'Keywords',    type: 'textarea',  span: 24 },
  //     { name: 'budget',     label: 'Budget',      type: 'currency',  span: 12 },
  //     { name: 'auditWindow',label: 'Audit Window',type: 'daterange', span: 12 },
  //   ],
  // },
  //
  // For a COMPLEX type (custom widgets/logic), add components like TaskIORequestFields /
  // TaskIODetailsView / TaskIOEditFields and reference them via `component` / `DetailsView` /
  // `EditFields` instead of `fields`.
  // ---------------------------------------------------------------------------
};

/**
 * Resolve a request type's definition with serialize/populate guaranteed.
 * Returns null for types that have no extra fields.
 */
export const getRequestTypeDef = (requestType) => {
  const def = registry[requestType];
  if (!def) return null;
  return {
    ...def,
    serialize: def.serialize || defaultSerialize(def.fields),
    populate: def.populate || defaultPopulate(def.fields),
  };
};

export { default as DynamicRequestDetails } from './DynamicRequestDetails';
export { default as DynamicRequestEditFields } from './DynamicRequestEditFields';
export { DynamicRequestFields };
export default registry;
